// Claim and scouting helpers shared by the Intel screen and the foreign system actions.
import type { ClaimantClaim, OwnerClaim } from '@stars-and-ships/api-types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '../api/queries';
import { formatDuration, formatNumber } from '../labels';
import { Button } from './ui';

export const OPEN_CLAIM = new Set(['pending', 'active']);

export const CLAIM_STATUS_LABELS: Record<ClaimantClaim['status'], string> = {
  pending: 'Pending',
  active: 'Active',
  fulfilled: 'Captured',
  cancelled: 'Cancelled',
  expired: 'Expired',
  invalidated: 'Invalidated',
};

export const until = (iso: string, now: number) => {
  const ms = Date.parse(iso) - now;
  return ms > 0 ? `in ${formatDuration(ms)}` : 'now';
};

/** Claim timing in one line: activation while pending, expiry once active. */
export function claimTiming(claim: ClaimantClaim | OwnerClaim, now: number): string {
  if (claim.status === 'pending') return `activates ${until(claim.activatesAt, now)}`;
  if (claim.status === 'active') return `active · expires ${until(claim.expiresAt, now)}`;
  return CLAIM_STATUS_LABELS[claim.status];
}

/** Cancelling refunds only part of the Credits (nothing once active): ask before doing it. */
export function CancelClaimButton({
  claim,
  disabled,
  label,
  onConfirm,
}: {
  claim: ClaimantClaim;
  disabled: boolean;
  label: string;
  onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <Button variant="secondary" disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  const refund = claim.cancelRefundCredits;
  return (
    <div
      data-cancel-confirm
      className="w-full space-y-2 hud-cut-sm border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
    >
      <p>
        {refund > 0
          ? `Cancelling refunds only ${formatNumber(refund)} of the ${formatNumber(claim.creditsPaid)} Credits you paid.`
          : `Cancelling refunds none of the ${formatNumber(claim.creditsPaid)} Credits you paid — the claim is already active.`}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => {
            setAsking(false);
            onConfirm();
          }}
        >
          Cancel claim anyway
        </Button>
        <Button variant="ghost" onClick={() => setAsking(false)}>
          Keep claim
        </Button>
      </div>
    </div>
  );
}

export function useInvalidateExpansion(galaxyId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.claims(galaxyId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.intel(galaxyId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.systems(galaxyId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.galaxyMe(galaxyId) });
  };
}
